import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, euro, formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flame, RefreshCw, FileDown, ExternalLink, Plus, AlertTriangle, BellRing, FileText, Send, Trash2 } from "lucide-react";
import { VenergiaLogo } from "@/components/VenergiaLogo";
import { EntityLink } from "@/components/EntityLink";

const COLOR = { RICEVUTA: "bg-amber-500", IN_FIRMA: "bg-blue-600", IN_LAVORAZIONE: "bg-purple-600", ATTIVA: "bg-teal-600", RESPINTA: "bg-red-600", CESSATA: "bg-slate-500", ANNULLATA: "bg-slate-500" };
const awaitingSignature = (r) => r.firma_mode === "CLIENTE" && (r.stato === "IN_FIRMA" || (r.stato === "RICEVUTA" && r.firma?.partita));
const fmt = (iso) => (iso || "").replace("T", " ").slice(0, 16);

function Nudge({ sel, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!awaitingSignature(sel)) return null;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/greenpower/pratiche/${sel.id}/sollecito`);
      toast.success(`Sollecito inviato${data.email ? " via email" : ""}${data.email && data.sms ? " e" : ""}${data.sms ? " via SMS" : ""}`);
      onDone({ ...sel, nudged_at: data.at, nudges: [...(sel.nudges || []), data] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sollecito non inviato"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1">
      <Button size="sm" variant="outline" className="gap-2" onClick={send} disabled={busy} data-testid="gp-nudge-btn"><BellRing className="w-4 h-4" /> {busy ? "Invio…" : "Sollecita firma al cliente"}</Button>
      {sel.nudged_at && <p className="text-xs text-muted-foreground" data-testid="gp-nudge-last">Ultimo sollecito: {fmt(sel.nudged_at)} ({(sel.nudges || []).length} in totale)</p>}
    </div>
  );
}

function Riepilogo({ sel, onDone }) {
  const [busy, setBusy] = useState(false);
  const sent = sel.riepilogo_inviato || [];
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/greenpower/pratiche/${sel.id}/riepilogo/invia`);
      toast.success(`Riepilogo inviato a ${data.to}`);
      onDone({ ...sel, riepilogo_inviato: [...sent, data] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        <a href={`${BACKEND_URL}/api/greenpower/pratiche/${sel.id}/riepilogo`} target="_blank" rel="noopener noreferrer" data-testid="gp-summary-pdf">
          <Button size="sm" variant="outline" className="gap-2"><FileText className="w-4 h-4" /> Riepilogo PDF</Button>
        </a>
        <Button size="sm" variant="outline" className="gap-2" onClick={send} disabled={busy || !sel.cliente_email} data-testid="gp-summary-send"><Send className="w-4 h-4" /> {busy ? "Invio…" : "Invia al cliente"}</Button>
      </div>
      {sent.length > 0 && <p className="text-xs text-muted-foreground" data-testid="gp-summary-last">Riepilogo inviato il {fmt(sent[sent.length - 1].at)} a {sent[sent.length - 1].to}</p>}
    </div>
  );
}

const when = (x) => new Date(x).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const FINAL = ["ATTIVA", "RESPINTA", "CESSATA", "ANNULLATA"];

const SyncLine = ({ sel, busy }) => {
  const auto = FINAL.includes(sel.stato) ? "" : " · si ricontrolla da sola ogni 15 minuti";
  const text = busy ? "Verifica con Venergia in corso…" : sel.sync_error ? `Verifica non riuscita il ${when(sel.sync_checked_at)}: ${sel.sync_error}${auto}` : sel.sync_checked_at ? `Verificata con Venergia il ${when(sel.sync_checked_at)}${auto}` : `Non ancora verificata con Venergia${auto}`;
  return <p className={`text-xs ${sel.sync_error && !busy ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`} data-testid="gp-detail-sync">{text}</p>;
};

const supply = (r) => [r.luce && `Luce ${r.luce.pod || ""}`, r.gas && `Gas ${r.gas.pdr || ""}`].filter(Boolean).join(" · ");

function DeleteBtn({ sel, onDeleted }) {
  if (!["RICEVUTA", "IN_FIRMA"].includes(sel.stato) || sel.pdf_firmato) return null;
  const del = async () => {
    if (!window.confirm("Eliminare la pratica? Andrà nel Cestino (ripristinabile entro 60 giorni) e verrà segnalata per l'annullamento su Venergia.")) return;
    try { await api.delete(`/greenpower/pratiche/${sel.id}`); toast.success("Pratica eliminata"); onDeleted(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  return <Button size="sm" variant="outline" className="gap-2 text-red-500" onClick={del} data-testid="gp-delete-btn"><Trash2 className="w-4 h-4" /> Elimina pratica</Button>;
}

function Detail({ sel, onRefresh, busy, isAdmin, onNudged, onDeleted }) {
  const commission = sel.commission_reversed || ["annullata", "stornata"].includes(sel.commission_status) ? "Provvigione annullata / stornata"
    : sel.commission_credited ? euro(sel.commission_amount || 0)
    : sel.commission_auto_paused || sel.commission_status === "sospesa" ? "Accrediti sospesi fino a conferma dell'amministrazione"
    : "In attesa della maturazione comunicata da Venergia";
  const mancano = sel.firma?.mancano || [];
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <Info label="N. pratica" value={sel.id_esterno} />
        <div data-testid="gp-detail-status"><Info label="Stato" value={sel.stato_label} /></div>
        <Info label="Firma" value={sel.firma_mode === "AGENZIA" ? "Firma diretta in agenzia" : "Firma del cliente"} />
        <Info label="Contratto" value={sel.contratto?.numero} />
        <Info label="Email cliente" value={sel.cliente_email} />
        <Info label="Fornitura" value={supply(sel)} />
        {isAdmin && <Info label="Dealer" value={<EntityLink id={sel.dealer_id} data-testid="gp-detail-dealer-link">{sel.dealer_name}</EntityLink>} />}
        <div data-testid="gp-detail-commission"><Info label="Provvigione" value={commission} /></div>
      </div>
      <SyncLine sel={sel} busy={busy} />
      {mancano.length > 0 && sel.stato === "RICEVUTA" && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="gp-detail-missing">
          <p className="font-medium text-amber-500 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Dati mancanti all'invio</p>
          <ul className="list-disc ml-5 mt-1">{mancano.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      <div>
        <p className="text-xs text-muted-foreground mb-1">Storico stati</p>
        {(sel.history || []).map((h, i) => <p key={i} className="text-xs" data-testid={`gp-history-${i}`}>{h.at ? fmt(h.at) : "Stato attuale"} — {h.label || h.stato}</p>)}
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        <Button size="sm" variant="outline" className="gap-2" onClick={onRefresh} disabled={busy} data-testid="gp-refresh-btn"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Verifica ora</Button>
        <Riepilogo sel={sel} onDone={onNudged} />
        <Nudge sel={sel} onDone={onNudged} />
        <DeleteBtn sel={sel} onDeleted={onDeleted} />
        {sel.pdf_firmato && (
          <a href={`${BACKEND_URL}/api/greenpower/pratiche/${sel.id}/documento`} target="_blank" rel="noopener noreferrer" data-testid="gp-pdf-link">
            <Button size="sm" variant="outline" className="gap-2"><FileDown className="w-4 h-4" /> Contratto firmato</Button>
          </a>
        )}
        {sel.link_completa && (
          <a href={sel.link_completa} target="_blank" rel="noopener noreferrer" data-testid="gp-complete-link">
            <Button size="sm" className="gap-2"><ExternalLink className="w-4 h-4" /> Completa nel gestionale</Button>
          </a>
        )}
      </div>
    </div>
  );
}

export default function GreenPowerPratiche() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("cerca") || "");
  const cerca = useSearchParams()[0].get("cerca");
  useEffect(() => { if (cerca) { setQ(cerca); } }, [cerca]);
  const [sel, setSel] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/greenpower/pratiche").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const check = async (id, manual) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/greenpower/pratiche/${id}/refresh`);
      setSel((cur) => (cur?.id === id ? data : cur)); load();
      if (manual && data.sync_error) toast.warning(data.sync_error);
      else if (manual) toast.success(`Verificata con Venergia: ${data.stato_label}`);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita";
      setSel((cur) => (cur?.id === id ? { ...cur, sync_error: msg, sync_checked_at: new Date().toISOString() } : cur));
      if (manual) toast.error(msg);
    } finally { setBusyId((cur) => (cur === id ? null : cur)); }
  };
  const refresh = () => check(sel.id, true);
  const selId = sel?.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (selId) { check(selId, false); } }, [selId]);

  const ql = q.trim().toLowerCase();
  const shown = rows.filter((r) => !ql || [r.cliente_nome, r.id_esterno, r.dealer_name, r.cliente_email].some((x) => (x || "").toLowerCase().includes(ql)));

  return (
    <div className="space-y-6" data-testid="gp-list-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <VenergiaLogo className="h-9 mb-3" />
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Flame className="w-7 h-7 text-primary" /> Pratiche Energia &amp; Gas</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isAdmin && <Link to="/app/energia"><Button className="gap-2" data-testid="gp-new-pratica"><Plus className="w-4 h-4" /> Nuova pratica</Button></Link>}
        </div>
      </div>
      <Input placeholder="Cerca per cliente, n. pratica, dealer…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="gp-search" />
      <div className="grid gap-3">
        {shown.map((r) => (
          <Card key={r.id} className="p-4 glow-border flex items-center justify-between gap-3 cursor-pointer hover:border-primary/50" onClick={() => setSel(r)} data-testid={`gp-row-${r.id}`}>
            <div className="min-w-0">
              <h3 className="font-head font-semibold truncate"><EntityLink kind="customer" id={r.customer_id} data-testid={`gp-customer-link-${r.id}`}>{r.cliente_nome}</EntityLink> · <EntityLink kind="energia" id={r.id} className="font-mono text-sm" data-testid={`gp-practice-link-${r.id}`}>{r.id_esterno}</EntityLink></h3>
              <p className="text-sm text-muted-foreground truncate">{supply(r)}{isAdmin && <> · dealer <EntityLink id={r.dealer_id} data-testid={`gp-dealer-link-${r.id}`}>{r.dealer_name}</EntityLink></>} · {(r.created_at || "").slice(0, 10)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {r.ambiente === "prova" && <Badge variant="outline">Prova</Badge>}
              {r.commission_reversed || ["annullata", "stornata"].includes(r.commission_status) ? <Badge variant="outline" className="text-red-500 border-red-500/40" data-testid={`gp-commission-label-${r.id}`}>{r.commission_status === "annullata" ? "Annullata" : "Stornata"}</Badge> : r.commission_credited && <Badge variant="outline" className="text-teal-500 border-teal-500/40" data-testid={`gp-commission-label-${r.id}`}>{euro(r.commission_amount || 0)}</Badge>}
              <Badge className={COLOR[r.stato] || "bg-muted"} data-testid={`gp-status-${r.id}`}>{r.stato_label}</Badge>
            </div>
          </Card>
        ))}
        {shown.length === 0 && <p className="text-muted-foreground" data-testid="gp-empty">Nessuna pratica.</p>}
      </div>
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><VenergiaLogo className="h-6 mb-2 self-start" /><DialogTitle>Pratica — {sel?.cliente_nome}</DialogTitle></DialogHeader>
          {sel && <Detail sel={sel} onRefresh={refresh} busy={busyId === sel.id} isAdmin={isAdmin} onNudged={(s) => { setSel(s); load(); }} onDeleted={() => { setSel(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium break-words">{value || "—"}</p></div>
);
